import React, { ComponentProps } from 'react';
import { useSonTranslation } from '../../translation/useSonTranslation';
import Btn from '../../components/molecules/Btn/Btn';
import { Icons } from '../../components/atoms/SVG';
import { TooltipPlacement } from '../../components/atoms/Tooltip';
import { HyperlinkTarget } from '../../components/atoms/Hyperlink';
import { FavoriteButton } from '../../components/forms/FavoriteButton/FavoriteButton';
import { Namespace } from '../../translation/namespace';
import { BtnModifier } from '../../components/molecules/Btn';
import ILink from '../../types/ILink';
import { asFetchableBlock } from '../../_lib/BlockFetcher';
import { BlockType } from '../../types/api/ElementType';

type PersonInteractionProps = {
  userFavorite: any;
  emailLink: ILink;
  phoneLink: ILink;
  conferenceAccessLink: ILink;
  profileName: string;
};

export function PersonInteraction({
  emailLink,
  phoneLink,
  profileName,
  conferenceAccessLink,
  userFavorite,
}: PersonInteractionProps) {
  const { t } = useSonTranslation('PersonInteraction');

  const getEmailButton = () => {
    if (emailLink?.url) {
      return (
        <div className="person-interaction__button-container">
          <Btn
            href={emailLink.url}
            svgSpriteId={Icons.MAIL_ENVELOPE}
            hiddenText
            titleText={t('email.title', { profileName })}
            tooltipPlacement={TooltipPlacement.TOP}
            cssClass="contact-person__button"
          >
            {t('email.hiddenText', { profileName })}
          </Btn>
        </div>
      );
    }
    return null;
  };

  const getPhoneButton = () => {
    if (phoneLink?.url) {
      return (
        <div className="person-interaction__button-container">
          <Btn
            href={phoneLink.url}
            svgSpriteId={Icons.PHONE}
            hiddenText
            titleText={t('phone.title', { profileName })}
            tooltipPlacement={TooltipPlacement.TOP}
            cssClass="contact-person__button"
          >
            {t('phone.hiddenText', { profileName })}
          </Btn>
        </div>
      );
    }

    return null;
  };

  const getConferenceButton = () => {
    if (conferenceAccessLink?.url) {
      return (
        <div className="person-interaction__button-container">
          <Btn
            href={conferenceAccessLink.url}
            svgSpriteId={Icons.VIDEOCALL}
            hiddenText
            titleText={t('conference.title')}
            tooltipPlacement={TooltipPlacement.TOP}
            cssClass="contact-person__button"
            target={HyperlinkTarget.BLANK}
            routing={false}
          >
            {t('conference.hiddenText', { profileName })}
          </Btn>
        </div>
      );
    }
    return null;
  };

  const getFavoriteUser = () => {
    return (
      userFavorite && (
        <FavoriteButton
          id="user-favorite-action-form"
          favorite={userFavorite}
          translationNamespace={Namespace.PersonInteraction}
          favoriteText={t('favorite.buttonText', { profileName })}
          noFavoriteText={t('noFavorite.buttonText', { profileName })}
          svgspriteId={Icons.CONTACTS_PHONEBOOK}
          color={BtnModifier.MINOR_ACTION_ACTIVATED_PETROL}
          resourceToInvalidate={BlockType.favoritedusers}
        />
      )
    );
  };

  if (getFavoriteUser() || getEmailButton() || getConferenceButton() || getPhoneButton()) {
    return (
      <div className="person-interaction">
        <h2 className="invisible">{t('headline', { name: profileName })}</h2>
        {getFavoriteUser()}
        {getEmailButton()}
        {getPhoneButton()}
        {getConferenceButton()}
      </div>
    );
  }
  return null;
}
export const PersonInteractionBlock = asFetchableBlock(
  PersonInteraction,
  BlockType.personinteraction,
  BlockType.personinteraction,
);

export type PersonInteractionBlock = ComponentProps<typeof PersonInteractionBlock>;
